import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import ReasonSelect from "../../../../components/inputs/ReasonSelect";

const TYPE_BY_KIND = {
  areaRatio: "MaturaAngleBisectorAreaRatioProofOpenTask",
  congruence: "MaturaTriangleCongruenceProofOpenTask",
  similarity: "MaturaTriangleSimilarityProofOpenTask",
};
const TASK_NUMBER_BY_KIND = { areaRatio: "21", congruence: "21.2", similarity: "21.3" };
const AREA_PAIRS = [[2, 3], [3, 5], [4, 7], [5, 8], [3, 4], [5, 6], [7, 9], [4, 9]];
const SIMILARITY_VARIANTS = [
  { mode: "direct", innerBase: 6, outerBase: 9, ratioNumerator: 2, ratioDenominator: 3 },
  { mode: "direct", innerBase: 6, outerBase: 10, ratioNumerator: 3, ratioDenominator: 5 },
  { mode: "direct", innerBase: 9, outerBase: 12, ratioNumerator: 3, ratioDenominator: 4 },
  { mode: "subtract", wholeLeft: 5, outerLeft: 3, wholeRight: 10, outerRight: 6, ratioNumerator: 2, ratioDenominator: 5 },
  { mode: "subtract", wholeLeft: 7, outerLeft: 3, wholeRight: 14, outerRight: 6, ratioNumerator: 4, ratioDenominator: 7 },
  { mode: "subtract", wholeLeft: 8, outerLeft: 3, wholeRight: 16, outerRight: 6, ratioNumerator: 5, ratioDenominator: 8 },
];
const pick = (items) => items[Math.floor(Math.random() * items.length)];

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

const METHOD_CHOICES = {
  areaRatio: [
    ["areaSine", "Wzór na pole z dwiema długościami boków i sinusem kąta między nimi"],
    ["equalHeights", "Równe wysokości wynikające z własności dwusiecznej kąta"],
    ["commonAltitude", "Wspólna wysokość i twierdzenie o dwusiecznej kąta"],
    ["similarity", "Podobieństwo trójkątów"],
  ],
  congruence: [
    ["congruence", "Przystawanie trójkątów"],
    ["similarity", "Podobieństwo trójkątów"],
    ["area", "Porównanie pól trójkątów"],
    ["pythagoras", "Twierdzenie Pitagorasa"],
  ],
  similarity: [
    ["similarity", "Podobieństwo trójkątów"],
    ["thales", "Twierdzenie Talesa"],
    ["congruence", "Przystawanie trójkątów"],
    ["area", "Porównanie pól trójkątów"],
  ],
};

const AREA_FORMULAS = [
  ["sineFormula", String.raw`P=\frac12 ab\sin\alpha`],
  ["cosineFormula", String.raw`P=\frac12 ab\cos\alpha`],
  ["sineNoHalf", String.raw`P=ab\sin\alpha`],
  ["heightNoHalf", String.raw`P=ah`],
];

const BRANCH_CHOICES = {
  areaRatio: {
    areaSine: AREA_FORMULAS,
    equalHeights: [
      ["bisectorDistance", "Każdy punkt dwusiecznej kąta jest równoodległy od jego ramion"],
      ["bisectorPerpendicular", "Dwusieczna kąta jest zawsze prostopadła do obu jego ramion"],
      ["sharedSideHeight", "Wspólny bok trójkątów jest jednocześnie ich wspólną wysokością"],
      ["equalAnglesEqualArea", "Trójkąty o jednej parze równych kątów mają równe pola"],
    ],
    commonAltitude: [
      ["bisectorTheorem", <InlineMath math={String.raw`\frac{|KN|}{|NL|}=\frac{|KM|}{|ML|}`} />],
      ["inverseBisector", <InlineMath math={String.raw`\frac{|KN|}{|NL|}=\frac{|ML|}{|KM|}`} />],
      ["squaredBisector", <InlineMath math={String.raw`\frac{|KN|}{|NL|}=\frac{|KM|^2}{|ML|^2}`} />],
      ["equalSegments", <InlineMath math={String.raw`|KN|=|NL|`} />],
    ],
    similarity: [
      ["aa", "Cecha kąt–kąt"],
      ["sas", "Cecha bok–kąt–bok"],
      ["sss", "Cecha bok–bok–bok"],
      ["rhs", "Cecha przeciwprostokątna–przyprostokątna"],
    ],
  },
  congruence: {
    congruence: [
      ["sas", "Cecha bok–kąt–bok"],
      ["asa", <>Kąty <InlineMath math={String.raw`\angle BAD`} /> i <InlineMath math={String.raw`\angle DAC`} /> są równe, podobnie jak kąty przy podstawie, a <InlineMath math="|AB|=|AC|" />.</>],
      ["sss", "Cecha bok–bok–bok"],
      ["rhs", <>Sama informacja o dwusiecznej nie pozwala jeszcze użyć cechy przeciwprostokątna–przyprostokątna.</>],
    ],
    similarity: [["aa", "Cecha kąt–kąt"], ["sasSimilar", "Dwa boki proporcjonalne i kąt między nimi"], ["sssSimilar", "Trzy pary boków proporcjonalnych"], ["angles", "Jedna para równych kątów"]],
    area: AREA_FORMULAS,
    pythagoras: [
      ["pythagoreanLegs", <>Dwusieczna <InlineMath math="AD" /> jest również wysokością, więc <InlineMath math={String.raw`|BD|^2=|AB|^2-|AD|^2`} /> oraz <InlineMath math={String.raw`|DC|^2=|AC|^2-|AD|^2`} />.</>],
      ["formulaOnly", <InlineMath math={String.raw`a^2+b^2=c^2`} />],
      ["wrongDifference", <InlineMath math={String.raw`|BD|^2=|AD|^2-|AB|^2`} />],
      ["hypotenuseOnly", <>Równość przeciwprostokątnych <InlineMath math="|AB|=|AC|" /> sama wystarcza do uznania trójkątów za przystające.</>],
    ],
  },
  similarity: {
    similarity: [
      ["aa", <>Kąt przy wierzchołku <InlineMath math="A" /> jest wspólny, a <InlineMath math={String.raw`\angle ADE=\angle ABC`} /> jako kąty odpowiadające, więc trójkąty są podobne na mocy cechy kąt–kąt.</>],
      ["oneAngle", "Jedna para równych kątów wystarcza do wykazania podobieństwa."],
      ["sssSimilar", "Trzy pary boków są proporcjonalne, co wynika bezpośrednio z danych."],
      ["equalSides", "Odcinek równoległy ma taką samą długość jak podstawa trójkąta."],
    ],
    thales: [],
    congruence: [["sas", "Cecha bok–kąt–bok"], ["sss", "Cecha bok–bok–bok"], ["asa", "Cecha kąt–bok–kąt"], ["rhs", "Cecha przeciwprostokątna–przyprostokątna"]],
    area: AREA_FORMULAS,
    pythagoras: [["ab", String.raw`a^2+b^2=c^2`], ["minus", String.raw`a^2-b^2=c^2`], ["sum", String.raw`a+b=c`], ["squares", String.raw`a^2+b^2=c`]],
  },
};

function areaConclusionChoices(method, a, b) {
  const byMethod = {
    areaSine: [
      ["cancelShared", <>Czynniki <InlineMath math={String.raw`\frac12`} />, <InlineMath math="MN" /> i <InlineMath math={String.raw`\sin\alpha`} /> są wspólne, więc po skróceniu pozostaje <InlineMath math={String.raw`\frac{${a}}{${b}}`} />.</>],
      ["anglesEqualAreas", "Równe kąty oznaczają, że pola obu trójkątów są równe"],
      ["sharedSideCongruent", "Wspólny bok oznacza, że trójkąty są przystające"],
      ["squareRatio", <>Stosunek pól jest kwadratem stosunku boków, czyli <InlineMath math={String.raw`\frac{${a ** 2}}{${b ** 2}}`} />.</>],
    ],
    equalHeights: [
      ["equalDistanceAreas", <>Dla równych wysokości <InlineMath math="d" /> pola mają postać <InlineMath math={String.raw`\frac12\cdot ${a}\cdot d`} /> i <InlineMath math={String.raw`\frac12\cdot ${b}\cdot d`} />.</>],
      ["equalHeightsEqualAreas", "Równe wysokości zawsze oznaczają równe pola"],
      ["heightIsMN", <>Wspólną wysokością obu trójkątów jest odcinek <InlineMath math="MN" />.</>],
      ["inverseHeightRatio", <>Stosunek pól jest odwrotny do stosunku podstaw: <InlineMath math={String.raw`\frac{${b}}{${a}}`} />.</>],
    ],
    commonAltitude: [
      ["commonAltitudeRatio", <>Wspólna wysokość z wierzchołka <InlineMath math="M" /> daje <InlineMath math={String.raw`\frac{P_{KNM}}{P_{NLM}}=\frac{|KN|}{|NL|}`} />, a twierdzenie o dwusiecznej kończy dowód.</>],
      ["commonAltitudeEqualAreas", "Wspólna wysokość oznacza równe pola niezależnie od podstaw"],
      ["altitudeSideRatio", <>Wspólna wysokość daje bezpośrednio stosunek <InlineMath math={String.raw`\frac{|KM|}{|ML|}`} /> bez użycia twierdzenia o dwusiecznej.</>],
      ["inverseBaseRatio", <>Stosunek pól jest równy <InlineMath math={String.raw`\frac{|NL|}{|KN|}`} />.</>],
    ],
    similarity: [["similarRatio", "Z podobieństwa wynika stosunek odpowiednich boków"], ["similarArea", "Z podobieństwa wynika kwadrat skali pól"], ["similarAngles", "Z podobieństwa wynikają równe kąty"], ["similarPerimeter", "Z podobieństwa wynika stosunek obwodów"]],
  };
  return byMethod[method] || [];
}

function thalesChoices(data) {
  if (data.similarityMode === "subtract") {
    const leftInner = data.wholeLeft - data.outerLeft;
    const rightInner = data.wholeRight - data.outerRight;
    return [
      ["subtractSegments", <><InlineMath math={String.raw`|AD|=|AB|-|DB|=${data.wholeLeft}-${data.outerLeft}=${leftInner}`} /> oraz <InlineMath math={String.raw`|AE|=|AC|-|EC|=${data.wholeRight}-${data.outerRight}=${rightInner}`} />, a następnie stosujemy twierdzenie Talesa.</>],
      ["useOuterSegments", <>W twierdzeniu Talesa podstawiamy bez odejmowania odcinki <InlineMath math="DB" /> i <InlineMath math="EC" /> zamiast <InlineMath math="AD" /> i <InlineMath math="AE" />.</>],
      ["subtractOpposite", <>Obliczamy <InlineMath math={String.raw`|AD|=|DB|-|AB|`} /> i <InlineMath math={String.raw`|AE|=|EC|-|AC|`} />.</>],
      ["equalParts", <>Równoległość <InlineMath math={String.raw`DE\parallel BC`} /> oznacza, że <InlineMath math="|AD|=|DB|" /> i <InlineMath math="|AE|=|EC|" />.</>],
    ];
  }
  return [
    ["directBaseRatio", <>Z twierdzenia Talesa otrzymujemy skalę <InlineMath math={String.raw`\frac{|DE|}{|BC|}=\frac{${data.innerBase}}{${data.outerBase}}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}`} />.</>],
    ["inverseBaseRatio", <>Skalę mniejszego trójkąta zapisujemy jako <InlineMath math={String.raw`\frac{|BC|}{|DE|}`} />.</>],
    ["baseDifference", <>Skalę podobieństwa obliczamy jako <InlineMath math={String.raw`\frac{|BC|-|DE|}{|BC|}`} />.</>],
    ["equalBases", <>Z równoległości wynika <InlineMath math="|DE|=|BC|" />.</>],
  ];
}

function similarityConclusionChoices(data) {
  return [
    ["ratioWithScale", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}`} />],
    ["inverseScale", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=\frac{${data.ratioDenominator}}{${data.ratioNumerator}}`} />],
    ["equalScale", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=1`} />],
    ["outerRatio", <InlineMath math={String.raw`\frac{|DB|}{|AB|}=\frac{|EC|}{|AC|}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}`} />],
  ];
}

const FINAL_CHOICES = {
  congruence: {
    congruence: [
      ["correspondingSides", <>Odpowiadające boki przystających trójkątów są równe, więc <InlineMath math="|BD|=|DC|" />.</>],
      ["allSidesEqual", <>Odcinek <InlineMath math="AD" /> jest wspólny, więc <InlineMath math="|AD|=|BD|=|DC|" />, a zatem <InlineMath math="|BD|=|DC|" />.</>],
      ["bisectorHalvesSide", <>Dwusieczna <InlineMath math="AD" /> dzieli bok <InlineMath math="BC" /> na połowy, więc <InlineMath math="|BD|=|DC|" />.</>],
      ["equalAreas", <>Trójkąty <InlineMath math="ABD" /> i <InlineMath math="ACD" /> mają wspólny bok, więc mają równe pola, a stąd <InlineMath math="|BD|=|DC|" />.</>],
    ],
    similarity: [["scaleOne", "Skala podobieństwa jest równa 1"], ["anglesOnly", "Odpowiadające kąty są równe"], ["sideRatio", "Odpowiadające boki są proporcjonalne"], ["areaRatio", "Pola są w stosunku kwadratu skali"]],
    area: [["equalArea", "Pola trójkątów są równe"], ["doubleArea", "Jedno pole jest dwa razy większe"], ["halfArea", "Jedno pole jest dwa razy mniejsze"], ["unknownArea", "Nie można porównać pól"]],
    pythagoras: [
      ["equalLegs", <>Prawe strony obu równań są równe, zatem <InlineMath math={String.raw`|BD|^2=|DC|^2`} />, a dla dodatnich długości <InlineMath math="|BD|=|DC|" />.</>],
      ["equalHyp", <>Z równości <InlineMath math="|AB|=|AC|" /> wynika jedynie równość przeciwprostokątnych.</>],
      ["right", "Na tym etapie wiemy tylko, że oba trójkąty są prostokątne."],
      ["sumLegs", <InlineMath math={String.raw`|BD|+|DC|=|BC|`} />],
    ],
  },
  similarity: {
    similarity: [["correspondingRatio", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}`} />], ["crossedRatio", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AC|}{|AE|}`} />], ["equalSides", <InlineMath math={String.raw`|AD|=|AB|`} />], ["squaredSides", <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|^2}{|AC|^2}`} />]],
    congruence: [["equalSides", "Odpowiadające boki są równe"], ["equalAngles", "Odpowiadające kąty są równe"], ["equalAreas", "Pola są równe"], ["equalPerimeters", "Obwody są równe"]],
    area: [["areaSquare", "Stosunek pól jest kwadratem skali"], ["areaLinear", "Stosunek pól jest równy skali"], ["areaEqual", "Pola są równe"], ["areaInverse", "Stosunek pól jest odwrotnością skali"]],
    pythagoras: [["right", "Oba trójkąty są prostokątne"], ["hyp", "Przeciwprostokątne są proporcjonalne"], ["legs", "Przyprostokątne są proporcjonalne"], ["notEnough", "Brakuje trójkąta prostokątnego"]],
  },
};

const CORRECT = {
  areaRatio: {
    areaSine: ["sineFormula", "cancelShared"],
    equalHeights: ["bisectorDistance", "equalDistanceAreas"],
    commonAltitude: ["bisectorTheorem", "commonAltitudeRatio"],
  },
  congruence: {
    congruence: [["sas", "asa"], "correspondingSides"],
    pythagoras: ["pythagoreanLegs", "equalLegs"],
  },
};

function asChoices(entries, order) {
  const indexed = Object.fromEntries(entries.map(([value, label]) => [value, { value, label: typeof label === "string" && label.startsWith("P=") ? <InlineMath math={label} /> : label }]));
  return (order || entries.map(([value]) => value)).map((value) => indexed[value]).filter(Boolean);
}

function methodEntries(kind) {
  return METHOD_CHOICES[kind].map(([value, label]) => ({ value, label }));
}

export function generateTask(options = {}) {
  const kind = options.proofKind || "areaRatio";
  const isOriginal = kind === "areaRatio" && options.variant === "original" && options.session === "2026_MAJ";
  const [a, b] = isOriginal ? [2, 3] : pick(AREA_PAIRS);
  const similarityVariant = kind === "similarity"
    ? ((options.variant === "original" && options.session === "2026_MAJ") ? SIMILARITY_VARIANTS[0] : pick(SIMILARITY_VARIANTS))
    : null;
  const methodOrder = shuffle(METHOD_CHOICES[kind].map(([value]) => value));
  const branchOrderByMethod = Object.fromEntries(Object.entries(BRANCH_CHOICES[kind]).map(([method, entries]) => [method, shuffle(entries.map(([value]) => value))]));
  if (similarityVariant) branchOrderByMethod.thales = shuffle(thalesChoices({ ...similarityVariant, similarityMode: similarityVariant.mode }).map(([value]) => value));
  const similarityFinals = similarityVariant ? similarityConclusionChoices(similarityVariant) : [];
  const finalSource = kind === "areaRatio"
    ? Object.fromEntries(METHOD_CHOICES.areaRatio.map(([method]) => [method, areaConclusionChoices(method, a, b)]))
    : kind === "similarity"
      ? { ...FINAL_CHOICES.similarity, similarity: similarityFinals, thales: similarityFinals }
      : FINAL_CHOICES[kind];
  const finalOrderByMethod = Object.fromEntries(Object.entries(finalSource).map(([method, entries]) => [method, shuffle(entries.map(([value]) => value))]));
  return {
    type: TYPE_BY_KIND[kind],
    data: {
      kind,
      a,
      b,
      maxPoints: 2,
      methodOrder,
      branchOrderByMethod,
      finalOrderByMethod,
      ...(similarityVariant ? { ...similarityVariant, similarityMode: similarityVariant.mode } : {}),
    },
    source: {
      variant: isOriginal ? "original" : "random",
      exam: "MATURA",
      session: options.session,
      taskNumber: TASK_NUMBER_BY_KIND[kind],
      basedOn: kind === "areaRatio" ? "MMAP-P0-100-A-2605-21" : "MATEMATRON-PROOF-PROTOTYPE",
    },
  };
}

export const generateCongruenceTask = (options = {}) => generateTask({ ...options, proofKind: "congruence" });
export const generateSimilarityTask = (options = {}) => generateTask({ ...options, proofKind: "similarity" });

function AreaDiagram({ a, b }) {
  return <svg viewBox="0 0 520 300" role="img" aria-label="Trójkąt KLM z dwusieczną MN" style={{ width: "min(100%, 520px)", margin: "8px auto", display: "block" }}><path d="M70 245 L450 245 L285 45 Z" fill="none" stroke="currentColor" strokeWidth="3"/><path d="M285 45 L272 245" fill="none" stroke="#ff9f1a" strokeWidth="4"/><path d="M252.1 75.6 A45 45 0 0 0 282 89.9 M282 89.9 A45 45 0 0 0 313.6 79.7" fill="none" stroke="#8b5cf6" strokeWidth="3"/><circle cx="272" cy="245" r="5" fill="#ff9f1a"/><g fill="currentColor" fontSize="22" fontFamily="KaTeX_Main, Cambria Math, Times New Roman, serif" fontStyle="italic"><text x="48" y="270">K</text><text x="455" y="270">L</text><text x="281" y="35">M</text><text x="262" y="272">N</text></g><g fill="#ff9f1a" fontSize="20" fontFamily="KaTeX_Main, Cambria Math, Times New Roman, serif"><text x="158" y="137">{a}</text><text x="386" y="140">{b}</text></g></svg>;
}

function CongruenceDiagram() {
  return <svg viewBox="0 0 420 300" role="img" aria-label="Trójkąt równoramienny ABC z dwusieczną AD" style={{ width: "min(100%, 420px)", margin: "8px auto", display: "block" }}><path d="M55 245 L365 245 L210 42 Z M210 42 L210 245" fill="none" stroke="currentColor" strokeWidth="3"/><path d="M184 76 A42 42 0 0 0 210 84 M210 84 A42 42 0 0 0 236 76" fill="none" stroke="#ff9f1a" strokeWidth="3"/><path d="M127 133 l10 8 M283 141 l10 -8" stroke="#8b5cf6" strokeWidth="3"/><g fill="currentColor" fontSize="22" fontFamily="KaTeX_Main, Cambria Math, Times New Roman, serif" fontStyle="italic"><text x="198" y="32">A</text><text x="35" y="270">B</text><text x="370" y="270">C</text><text x="202" y="274">D</text></g></svg>;
}

function SimilarityDiagram({ data }) {
  const { ratioNumerator, ratioDenominator } = data;
  const scale = ratioNumerator / ratioDenominator;
  const y = 35 + (270 - 35) * scale;
  const leftX = 215 + (55 - 215) * scale;
  const rightX = 215 + (375 - 215) * scale;
  return <svg viewBox="0 0 430 320" role="img" aria-label="Trójkąt ABC z odcinkiem DE równoległym do BC" style={{ width: "min(100%, 430px)", margin: "8px auto", display: "block" }}><path d="M215 35 L55 270 L375 270 Z" fill="none" stroke="currentColor" strokeWidth="3"/><path d={`M${leftX} ${y} L${rightX} ${y}`} fill="none" stroke="#ff9f1a" strokeWidth="4"/><g fill="currentColor" fontSize="22" fontFamily="KaTeX_Main, Cambria Math, Times New Roman, serif" fontStyle="italic"><text x="205" y="25">A</text><text x="35" y="292">B</text><text x="380" y="292">C</text><text x={leftX - 24} y={y - 8}>D</text><text x={rightX + 8} y={y - 8}>E</text></g><g fill="#ff9f1a" fontSize="18" fontFamily="KaTeX_Main, Cambria Math, Times New Roman, serif">{data.similarityMode === "direct" ? <><text x={215} y={y - 8} textAnchor="middle">{data.innerBase}</text><text x="215" y="296" textAnchor="middle">{data.outerBase}</text></> : <><text x={leftX - 36} y={(y + 270) / 2}>{data.outerLeft}</text><text x={rightX + 24} y={(y + 270) / 2}>{data.outerRight}</text><text x="118" y="145">|AB|={data.wholeLeft}</text><text x="282" y="145">|AC|={data.wholeRight}</text></>}</g></svg>;
}

export function renderTask({ data }) {
  if (data.kind === "congruence") return <div><p>W trójkącie równoramiennym <InlineMath math="ABC" /> zachodzi <InlineMath math="|AB|=|AC|" />. Odcinek <InlineMath math="AD" /> jest dwusieczną kąta <InlineMath math="BAC" />, a punkt <InlineMath math="D" /> leży na boku <InlineMath math="BC" />.</p><CongruenceDiagram/><p>Wykaż, że <InlineMath math="|BD|=|DC|" />.</p></div>;
  if (data.kind === "similarity") return <div><p>W trójkącie <InlineMath math="ABC" /> punkty <InlineMath math="D" /> i <InlineMath math="E" /> leżą odpowiednio na bokach <InlineMath math="AB" /> i <InlineMath math="AC" />, a <InlineMath math={String.raw`DE\parallel BC`} />.</p>{data.similarityMode === "direct" ? <p>Dane są długości <InlineMath math={String.raw`|DE|=${data.innerBase}`} /> oraz <InlineMath math={String.raw`|BC|=${data.outerBase}`} />.</p> : <p>Dane są długości <InlineMath math={String.raw`|AB|=${data.wholeLeft}`} />, <InlineMath math={String.raw`|DB|=${data.outerLeft}`} />, <InlineMath math={String.raw`|AC|=${data.wholeRight}`} /> oraz <InlineMath math={String.raw`|EC|=${data.outerRight}`} />.</p>}<SimilarityDiagram data={data}/><p>Wykaż, że</p><BlockMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}.`}/></div>;
  return <div><p>Dany jest trójkąt <InlineMath math="KLM" />, w którym</p><BlockMath math={String.raw`|KM|=${data.a},\qquad |LM|=${data.b}.`}/><p>Punkt <InlineMath math="N" /> leży na boku <InlineMath math="KL" />, a odcinek <InlineMath math="MN" /> jest dwusieczną kąta <InlineMath math="KML" />.</p><AreaDiagram a={data.a} b={data.b}/><p>Wykaż, że</p><BlockMath math={String.raw`\frac{P_{KNM}}{P_{NLM}}=\frac{${data.a}}{${data.b}}.`}/></div>;
}

function branchChoices(data, method) {
  if (data.kind === "similarity" && method === "thales") {
    return asChoices(thalesChoices(data), data.branchOrderByMethod.thales);
  }
  return asChoices(BRANCH_CHOICES[data.kind][method] || [], data.branchOrderByMethod[method]);
}

function finalChoices(data, method) {
  const entries = data.kind === "areaRatio"
    ? areaConclusionChoices(method, data.a, data.b)
    : data.kind === "similarity" && ["similarity", "thales"].includes(method)
      ? similarityConclusionChoices(data)
      : FINAL_CHOICES[data.kind][method] || [];
  return asChoices(entries, data.finalOrderByMethod[method]);
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const { data } = task;
  const setMethod = (method) => onChange?.({ method, branch: "", conclusion: "" });
  const setBranch = (branch) => onChange?.({ ...userAnswer, branch, conclusion: "" });
  return <div style={{ display: "grid", gap: 18, marginTop: 12, textAlign: "left" }}>
    <div style={{ display: "grid", gap: 8 }}><div><strong>Krok 1.</strong> Wybierz metodę dowodu.</div><ReasonSelect choices={data.methodOrder.map((value) => methodEntries(data.kind).find((choice) => choice.value === value))} value={userAnswer.method || ""} onChange={setMethod} disabled={disabled} placeholder="Wybierz metodę" ariaLabel="Metoda dowodu"/></div>
    <div style={{ display: "grid", gap: 8 }}><div><strong>Krok 2.</strong> Wybierz właściwy wzór, cechę albo przesłankę dla tej metody.</div><ReasonSelect choices={branchChoices(data, userAnswer.method)} value={userAnswer.branch || ""} onChange={setBranch} disabled={disabled || !userAnswer.method} placeholder="Wybierz przesłankę" disabledLabel="Najpierw wybierz metodę w kroku 1" ariaLabel="Przesłanka dowodu"/></div>
    <div style={{ display: "grid", gap: 8 }}><div><strong>Krok 3.</strong> Wybierz poprawne domknięcie rozumowania.</div><ReasonSelect choices={finalChoices(data, userAnswer.method)} value={userAnswer.conclusion || ""} onChange={(conclusion) => onChange?.({ ...userAnswer, conclusion })} disabled={disabled || !userAnswer.branch} placeholder="Wybierz wniosek" disabledLabel="Najpierw wybierz przesłankę w kroku 2" ariaLabel="Wniosek dowodu"/></div>
  </div>;
}

export function checkAnswer({ data }, value = {}) {
  if (data.kind === "similarity") {
    const expectedBranch = value.method === "similarity"
      ? "aa"
      : value.method === "thales"
        ? (data.similarityMode === "subtract" ? "subtractSegments" : "directBaseRatio")
        : null;
    const method = Boolean(expectedBranch);
    const branch = Boolean(method && value.branch === expectedBranch);
    const conclusion = Boolean(branch && value.conclusion === "ratioWithScale");
    return {
      correct: conclusion,
      score: Number(branch) + Number(conclusion),
      maxScore: 2,
      details: { method, branch, conclusion },
    };
  }
  const expected = CORRECT[data.kind]?.[value.method];
  const acceptedBranches = expected
    ? (Array.isArray(expected[0]) ? expected[0] : [expected[0]])
    : [];
  const method = Boolean(expected);
  const branch = acceptedBranches.includes(value.branch);
  const conclusion = Boolean(branch && value.conclusion === expected[1]);
  return { correct: conclusion, score: Number(branch) + Number(conclusion), maxScore: 2, details: { method, branch, conclusion } };
}

function correctPath(data) {
  if (data.kind === "congruence") return { method: "congruence", branch: "sas", conclusion: "correspondingSides" };
  if (data.kind === "similarity") return { method: "similarity", branch: "aa", conclusion: "ratioWithScale" };
  return { method: "areaSine", branch: "sineFormula", conclusion: "cancelShared" };
}

export function renderAnswer({ data }) {
  if (data.kind === "congruence") return <><InlineMath math={String.raw`\triangle ABD\equiv\triangle ACD`} /> na mocy cechy bok–kąt–bok, zatem <InlineMath math="|BD|=|DC|" />.</>;
  if (data.kind === "similarity") return <><InlineMath math={String.raw`\triangle ADE\sim\triangle ABC`} /> na mocy cechy kąt–kąt, zatem <InlineMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}`} />.</>;
  return <div style={{ display: "grid", gap: 6 }}><InlineMath math={String.raw`P_{KNM}=\frac12\cdot ${data.a}\cdot |MN|\cdot\sin\alpha`}/><InlineMath math={String.raw`P_{NLM}=\frac12\cdot ${data.b}\cdot |MN|\cdot\sin\alpha`}/><InlineMath math={String.raw`\frac{P_{KNM}}{P_{NLM}}=\frac{${data.a}}{${data.b}}`}/></div>;
}

export function renderUserAnswer(value = {}, task) {
  if (!value.method && !value.branch && !value.conclusion) return <i>(brak odpowiedzi)</i>;
  return <div style={{ display: "grid", gap: 4 }}><span><strong>Metoda:</strong> {value.method || "--"}</span><span><strong>Przesłanka:</strong> {value.branch || "--"}</span><span><strong>Wniosek:</strong> {value.conclusion || "--"}</span></div>;
}

export function renderExplanation({ data }) {
  if (data.kind === "congruence") return <div><p><strong>Sposób 1: bok–kąt–bok.</strong> Mamy <InlineMath math="|AB|=|AC|" />, wspólny bok <InlineMath math="AD" /> oraz równe kąty <InlineMath math={String.raw`\angle BAD=\angle DAC`} />. Trójkąty <InlineMath math="ABD" /> i <InlineMath math="ACD" /> są więc przystające.</p><p><strong>Sposób 2: kąt–bok–kąt.</strong> Kąty <InlineMath math={String.raw`\angle BAD`} /> i <InlineMath math={String.raw`\angle DAC`} /> są równe, a w trójkącie równoramiennym równe są także kąty przy podstawie: <InlineMath math={String.raw`\angle ABD=\angle ACD`} />. Pomiędzy nimi leżą równe boki <InlineMath math="AB" /> i <InlineMath math="AC" />, więc trójkąty są przystające.</p><p><strong>Sposób 3: twierdzenie Pitagorasa.</strong> Dwusieczna opuszczona z wierzchołka trójkąta równoramiennego jest również wysokością, zatem oba trójkąty są prostokątne.</p><BlockMath math={String.raw`|BD|^2=|AB|^2-|AD|^2,\qquad |DC|^2=|AC|^2-|AD|^2`}/><p>Ponieważ <InlineMath math="|AB|=|AC|" />, prawe strony są równe. Długości są dodatnie, więc <InlineMath math="|BD|=|DC|" />.</p><p><strong>Wniosek.</strong> Każda z poprawnych ścieżek prowadzi do <InlineMath math="|BD|=|DC|" />.</p></div>;
  if (data.kind === "similarity") return <div><p><strong>Sposób 1: podobieństwo.</strong> Kąt przy wierzchołku <InlineMath math="A" /> jest wspólny, a z równoległości <InlineMath math={String.raw`DE\parallel BC`} /> wynika równość odpowiednich kątów. Trójkąty <InlineMath math="ADE" /> i <InlineMath math="ABC" /> są podobne na mocy cechy kąt–kąt.</p><p><strong>Sposób 2: twierdzenie Talesa.</strong> Równoległość odcinków pozwala bezpośrednio zapisać proporcje odpowiednich części boków.</p>{data.similarityMode === "subtract" && <><p>Najpierw obliczamy brakujące części boków:</p><BlockMath math={String.raw`|AD|=${data.wholeLeft}-${data.outerLeft}=${data.wholeLeft - data.outerLeft},\qquad |AE|=${data.wholeRight}-${data.outerRight}=${data.wholeRight - data.outerRight}.`}/></>}<p><strong>Wniosek.</strong></p><BlockMath math={String.raw`\frac{|AD|}{|AB|}=\frac{|AE|}{|AC|}=\frac{|DE|}{|BC|}=\frac{${data.ratioNumerator}}{${data.ratioDenominator}}.`}/></div>;
  return <div><p><strong>Krok 1.</strong> Dwusieczna daje równe kąty. Oznaczmy ich miarę przez <InlineMath math={String.raw`\alpha`} />.</p><p><strong>Krok 2.</strong> Zapisujemy oba pola:</p><BlockMath math={String.raw`P_{KNM}=\frac12\cdot ${data.a}\cdot |MN|\cdot\sin\alpha`}/><BlockMath math={String.raw`P_{NLM}=\frac12\cdot ${data.b}\cdot |MN|\cdot\sin\alpha`}/><p><strong>Krok 3.</strong> Skracamy wspólne czynniki.</p><BlockMath math={String.raw`\frac{P_{KNM}}{P_{NLM}}=\frac{\frac12\cdot ${data.a}\cdot |MN|\cdot\sin\alpha}{\frac12\cdot ${data.b}\cdot |MN|\cdot\sin\alpha}=\frac{${data.a}}{${data.b}}`}/></div>;
}

export function generatePrototypeTasks(options = {}) {
  return ["areaRatio", "congruence", "similarity"].map((proofKind) => generateTask({ ...options, proofKind }));
}

export const getCorrectPath = (task) => correctPath(task.data);
